import { IPromise } from 'angular';
import * as _ from 'underscore';
import { CommonDatasourceInfo } from '../../app/spaces/CommonDatasourceInfo';
import { Messages } from '../../codegen/Localize';
import { imgPath } from '../../common/utilities/ImgUrl';
import { FeatureFlags } from '../FeatureFlags';
import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { ListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { DatasourceMapper } from '../mappers/DatasourceMapper';
import { DatasourceDefinition } from '../models/ContentDefinitions';
import { IDataConnectionInfo } from '../models/IDataConnectionInfo';
import { ExtractEncryptionState, IDataSourceDetailInfo, IDataSourceInfo } from '../models/IDataSourceInfo';
import { ITaggableContent } from '../models/ITaggableContent';
import { ICertifiableItemInfo, IExplorableItemTypeInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { AddTagsToDatasourcesRequest } from '../ServerApi/requests/AddTagsToDatasourcesRequest';
import { ChangeExtractToLiveActionOnDatasourcesRequest } from '../ServerApi/requests/ChangeExtractToLiveActionOnDatasourcesRequest';
import { CreateExtractTasksRequest } from '../ServerApi/requests/CreateExtractTasksRequest';
import { DeleteDatasourcesRequest } from '../ServerApi/requests/DeleteDatasourcesRequest';
import { GetDatasourceActionsRequest } from '../ServerApi/requests/GetDatasourceActionsRequest';
import { GetDatasourcesRequest } from '../ServerApi/requests/GetDatasourcesRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import {
  GetPreferredRefreshOptionByUrlNameRequest,
  GetPreferredRefreshOptionRequest,
  IGetPreferredRefreshOptionByUrlNameResult,
  IGetPreferredRefreshOptionResult,
} from '../ServerApi/requests/GetPreferredRefreshOption';
import { MoveDatasourcesToProjectRequest } from '../ServerApi/requests/MoveDatasourcesToProjectRequest';
import { PerformExtractEncryptionActionOnDatasourcesRequest } from '../ServerApi/requests/PerformExtractEncryptionActionOnDatasourcesRequest';
import { RemoveTagsFromDatasourcesRequest } from '../ServerApi/requests/RemoveTagsFromDatasourcesRequest';
import { RunCreateExtractsOnDatasourcesRequest } from '../ServerApi/requests/RunCreateExtractsOnDatasourcesRequest';
import { RunExtractRefreshesOnDatasourcesRequest } from '../ServerApi/requests/RunExtractRefreshesOnDatasourcesRequest';
import {
  ISetDatasourceCertificationParams,
  SetDatasourceCertificationRequest,
} from '../ServerApi/requests/SetDatasourceCertificationRequest';
import { SetDatasourceDescriptionRequest } from '../ServerApi/requests/SetDatasourceDescriptionRequest';
import {
    ISetDatasourceNlpSettingParams,
    SetDatasourceNlpSettingRequest,
} from '../ServerApi/requests/SetDatasourceNlpSettingRequest';
import {
  ISetDatasourceRefreshModeParams,
  SetDatasourceRefreshModeRequest,
} from '../ServerApi/requests/SetDatasourceRefreshModeRequest';
import {
  ISetDatasourceRemoteAgentParams,
  SetDatasourceRemoteAgentRequest,
} from '../ServerApi/requests/SetDatasourceRemoteAgentRequest';
import {
  ISetDatasourceRemoteAgentReservationParams,
  ISetDatasourceRemoteAgentReservationResult,
  SetDatasourceRemoteAgentReservationRequest,
} from '../ServerApi/requests/SetDatasourceRemoteAgentReservationRequest';
import {
  ISetDatasourceRemoteQueryAgentParams,
  SetDatasourceRemoteQueryAgentRequest,
} from '../ServerApi/requests/SetDatasourceRemoteQueryAgentRequest';
import {
  ISetDatasourceUsingRemoteQueryAgentParams,
  SetDatasourceUsingRemoteQueryAgentRequest,
} from '../ServerApi/requests/SetDatasourceUsingRemoteQueryAgentRequest';

import { getDataConnectionInfos } from '../../app/sagas/spaces/EditConnectionSaga';
import { ServiceLocator } from '../../app/ServiceLocator';
import { RemoteConnectionDetailInfo } from '../models/ConnectionDetailInfo';
import { DatasourceContentType } from '../models/ContentTypes';
import { CancelExtractCreationRequest } from '../ServerApi/requests/CancelExtractCreationRequest';
import { ICheckConnectionResult } from '../ServerApi/requests/CheckConnectionRequest';
import { SetDatasourcesOwnerRequest } from '../ServerApi/requests/SetDatasourcesOwnerRequest';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { ExtractTaskTargetType } from '../ServerApi/types/IExtractTask';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import { DataConnections, IConnectionUpdate } from './DataConnections';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';
import { ExtractEncryptionMode } from './Sites';

// Map of data source icons for each size.
// This keeps the image file paths intact and keeps logic simple
// 'medium' is used for list view and the workbook card tooltip
export type DatasourceIconSize = 'medium' | 'masthead' | 'card';
type DatasourceImagesPerSize = Record<DatasourceIconSize, string>;
type DatasourceIconType = 'embedded' | 'extract' | 'extract_certified' | 'live' | 'live_certified';
const DatasourceIcons: Record<DatasourceIconType, DatasourceImagesPerSize> = {
  embedded : {
    'medium'   : 'spaces_medium_ds_embedded.svg',
    'masthead' : 'spaces_masthead_ds_embedded.svg',
    'card'     : 'spaces_card_ds_embedded.svg'
  },
  extract : {
    'medium'   : 'spaces_medium_extract.svg',
    'masthead' : 'spaces_masthead_extract.svg',
    'card'     : 'spaces_card_extract.svg'
  },
  extract_certified : {
    'medium'   : 'spaces_medium_extract_certified.svg',
    'masthead' : 'spaces_masthead_extract_certified.svg',
    'card'     : 'spaces_card_extract_certified.svg'
  },
  live : {
    'medium'   : 'spaces_medium_ds.svg',
    'masthead' : 'spaces_card_ds.svg',  // this doesn't look good left-aligned in masthead so use the centered one
    'card'     : 'spaces_card_ds.svg'
  },
  live_certified : {
    'medium'    : 'spaces_medium_ds_certified.svg',
    'masthead'  : 'spaces_masthead_ds_certified.svg',
    'card'      : 'spaces_card_ds_certified.svg'
  }
};

export class DataSources implements IListResource<IDataSourceInfo>, IFieldValuesResource, IDetailResource<IDataSourceDetailInfo>, IActionResource<IDatasourceActions>, ITaggableContent {

  public static ServiceLocatorKey = 'DataSources';

  public static isPublished(datasource: CommonDatasourceInfo): boolean {
    return !datasource.workbook;
  }

  public static isEmbedded(datasource: CommonDatasourceInfo): boolean {
    return !DataSources.isPublished(datasource);
  }

  public static isExtract(datasource: CommonDatasourceInfo): boolean {
    return datasource.hasExtracts;
  }

  public static isUnencrypted(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.unencrypted;
  }
  public static isEncrypted(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.encrypted;
  }

  public static isPendingCreateExtract(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractCreationPending;
  }

  public static isPendingEncryption(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.pendingEncryption;
  }

  public static isPendingDecryption(datasource: CommonDatasourceInfo): boolean {
    return datasource.extractEncryptionState === ExtractEncryptionState.pendingDecryption;
  }

  public static getDatasourceInfoFromDataConnection(dataconnection: IDataConnectionInfo): IDataSourceInfo {
    // We want to treat the datasource property of a data connection object retrieved from GetDataConnections as a UnifiedDatasource
    // The id is a unified Id data source and we used the certificationDetails property to test for this. See comment in isUnifiedDatasource
    return {...dataconnection.datasource, contentType: 'datasource', certificationDetails: {} as ICertifiableItemInfo} as IDataSourceInfo;
  }

  public static isUnifiedDatasource(contentItem: IExplorableItemTypeInfo): contentItem is IDataSourceInfo {
    // There are two SOLR Cores that have data sources: the unified data source core and the content core
    // The content core has different ids for data sources than the unified data source core.
    // The data source core stores certificationDetails as a separate property for each data source and
    // the data source maps it back to the UI code. The content core maps the certification details into
    // the property 'contentDetail.' So, here we can check if the item has 'certificationDetails' on it
    // know if it's from the unified data source core.
    // getDatasource and getDatasources are from the unified data source core; exploreContent is from
    // the content core.
    return contentItem.contentType
        && contentItem.contentType === DatasourceDefinition.contentType
        && contentItem.hasOwnProperty('certificationDetails');
  }

  public static isCertified(contentItem: CommonDatasourceInfo): boolean {
    return FeatureFlags.datasourceCertificationEnabled
      && contentItem.certificationDetails && contentItem.certificationDetails.isCertified;
  }

  public static hasDataQualityWarning(contentItem: CommonDatasourceInfo): boolean {
    return contentItem.dataQualityIndicators
        && contentItem.dataQualityIndicators[0]
        && contentItem.dataQualityIndicators[0].isActive;
  }

  public static getIconForDatasourceType(datasourceItem: CommonDatasourceInfo, size: DatasourceIconSize): string {

    if (DataSources.isEmbedded(datasourceItem)) {
      return imgPath(DatasourceIcons['embedded'][size]);
    }

    if (DataSources.isExtract(datasourceItem)) {
      if (DataSources.isCertified(datasourceItem)) {
        return imgPath(DatasourceIcons['extract_certified'][size]);
      } else {
        return imgPath(DatasourceIcons['extract'][size]);
      }
    } else { // Live Datasource
      if (DataSources.isCertified(datasourceItem)) {
        return imgPath(DatasourceIcons['live_certified'][size]);
      } else {
        return imgPath(DatasourceIcons['live'][size]);
      }
    }
  }

  public static ExtractStatusOrLive(isExtractEncryptionEnabled: boolean, hasExtracts: boolean,
    extractEncryptionState: ExtractEncryptionState, extractCreationPending: boolean, siteEaRMode: ExtractEncryptionMode): string {
    const messages = new Map<ExtractEncryptionState, string>([
      [ExtractEncryptionState.encrypted, Messages.ear_Extract_Encryption_State_Encrypted()],
      [ExtractEncryptionState.pendingDecryption, FeatureFlags.extractMoveToTheWebEnabled ? Messages.ear_Extract_Encryption_State_Decrypting_mttw():Messages.ear_Extract_Encryption_State_Decrypting()],
      [ExtractEncryptionState.pendingEncryption, FeatureFlags.extractMoveToTheWebEnabled ? Messages.ear_Extract_Encryption_State_Encrypting_mttw():Messages.ear_Extract_Encryption_State_Encrypting()],
      [ExtractEncryptionState.unencrypted, siteEaRMode === ExtractEncryptionMode.DISABLED ? Messages.extract() : Messages.ear_Extract_Encryption_State_Unencrypted()]
    ]);

    // ExtractMoveToTheWebEnabled, will use new message to replace the existing message
    // if the hasExtracts is true, no necessary to check extractCreationPending
    if(!hasExtracts && extractCreationPending) {
      return Messages.ExtractCreationPending();
    }

    // If the feature is enabled show the user the state of extracts
    if(isExtractEncryptionEnabled && hasExtracts) {
      const result = messages.get(extractEncryptionState);
      if(!_.isUndefined(result)) {
        return result;
      }
    }

    return hasExtracts? Messages.extract() : Messages.live();
  }

  public static $inject = [
    '$q',
    'ErrorNotifyingServer',
    'FetcherFactory',
    'DataConnections'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IDataSourceInfo>;
  private allQuery: ResourceQuery<IDataSourceInfo>;
  private detailFetcher: IDetailFetcher<IDataSourceDetailInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor(
    private $q: ng.IQService,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory,
    private DataConnections: DataConnections
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetDatasourcesRequest, ServerApiMapper.dataSources);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getDatasource', DatasourceMapper.mapDetail);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<IDataSourceInfo> {
    return this.allQuery;
  }

  public published(): ResourceQuery<IDataSourceInfo> {
    return this.all().addFilter(Filter.fieldEq(FilterFieldNames.isPublished, true));
  }

  public getByWorkbookId(id: string): ng.IPromise<ListResult<IDataSourceInfo>> {
    const query = this.all().addFilter(Filter.fieldEq(FilterFieldNames.workbookId, id));
    return SliceableHelper.fetchAll(query);
  }

  public getById(id: string): ng.IPromise<IDataSourceDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  /**
   * Takes one or more IContentInfo objects and returns either:
   *  1) the corresponding IDataSourceInfo objects, fetched from the API.
   *  2) the same objects, if they're already IDataSourceInfo objects.
   *
   * If IDataSourceInfo objects are fetched from the API, datasource IDs are first mapped to
   * unified datasource IDs (eg: connection IDs).
   *
   * The function only evaulates the first content info item to decide which approach is needed.
   *
   * @param content One or more IContentInfo objects.
   */
  public fromContent(...content: IExplorableItemTypeInfo[]): ng.IPromise<IDataSourceInfo[]> {
    const initialIdArray = content.map(d => d.id);

    if (content && content.length > 0) {
      if (DataSources.isUnifiedDatasource(content[0])) {
        // If the first one is already a unified data source, assume they all are.
        return Q.when(content as IDataSourceInfo[]) as ng.IPromise<IDataSourceInfo[]>;
      } else {
        // The getDatasource endpoint still expects unified datasource IDs, which are really connection IDs.
        const datasourcesResource = this;
        return this.DataConnections.getConnectionIdsFromDatasourceIds(initialIdArray).then(map => {
          const unifiedDatasourceIds = _.values(map).map(v => v[0]);
          return datasourcesResource.all()
                                    .addFilter(Filter.stringFieldInArray('id', unifiedDatasourceIds))
                                    .getSlice(0, unifiedDatasourceIds.length)
                                    .then(result => result.items);
        });
      }
    } else {
      return Q.when([]) as ng.IPromise<IDataSourceInfo[]>;
    }
  }

  public getPublishedByUrlNames(urlNames: string[]): ng.IPromise<ListResult<IDataSourceInfo>> {
    const query = this.all()
      .addFilter(Filter.stringFieldInArray(FilterFieldNames.urlName, urlNames))
      .addFilter(Filter.fieldEq(FilterFieldNames.isPublished, true));

    return SliceableHelper.fetchAll(query);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'datasource');
  }

  // Actions

  public actions(id: string): ng.IPromise<IDatasourceActions> {
    return this.server.sendRequest(new GetDatasourceActionsRequest(id));
  }

  public del(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteDatasourcesRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setCertification(params: ISetDatasourceCertificationParams): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceCertificationRequest(params));
  }

  public setDescription(id: string, description: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceDescriptionRequest(id, description));
  }

  public setNlpSetting(params: ISetDatasourceNlpSettingParams): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceNlpSettingRequest(params));
  }

  public setOwner(ids: string[], ownerId: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetDatasourcesOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public setRefreshMode(id: string, refreshMode: string): ng.IPromise<IResult> {
    const params: ISetDatasourceRefreshModeParams = {
      id: id,
      refreshMode: refreshMode
    };

    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceRefreshModeRequest(params));
  }

  public setRemoteAgent(id: string, remoteAgentName: string, workflowId?: string): ng.IPromise<IResult> {
    const params: ISetDatasourceRemoteAgentParams = {
      id: id,
      remoteAgentName: remoteAgentName,
      workflowId: workflowId
    };

    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceRemoteAgentRequest(params));
  }

  public setRemoteAgentReservation(id: string, remoteAgentReservationId: string): ng.IPromise<ISetDatasourceRemoteAgentReservationResult> {
    const params: ISetDatasourceRemoteAgentReservationParams = {
      id: id,
      remoteAgentReservationId: remoteAgentReservationId
    };

    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceRemoteAgentReservationRequest(params));
  }

  public moveToProject(targetProjectId: string, datasourceIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(datasourceIds, (idsBlock) => new MoveDatasourcesToProjectRequest({
        ids: idsBlock,
        projectId: targetProjectId
      })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): ng.IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToDatasourcesRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public removeTags(ids: string[], tags: string[]): ng.IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromDatasourcesRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): ng.IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public createExtractTask(ids: string[], scheduleId: string, extractType: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new CreateExtractTasksRequest({
        targetType: ExtractTaskTargetType.Datasource,
        targetIds: idsBlock,
        scheduleId: scheduleId,
        type: extractType // ExtractTaskType
      })),
      ResultHelper.combineResults);
  }

  public refreshExtracts(ids: string[], extractType: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new RunExtractRefreshesOnDatasourcesRequest(idsBlock, extractType)
      ),
      ResultHelper.combineResults);
  }

  public createExtracts(ids: string[], encrypt: boolean): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new RunCreateExtractsOnDatasourcesRequest(idsBlock, encrypt)
      ),
      ResultHelper.combineResults);
  }

  public encryptDataSources(ids: string[], actionType: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnDatasourcesRequest(idsBlock, actionType)
      ),
    ResultHelper.combineResults);
  }

  public getPreferredRefreshOption(id: string): ng.IPromise<IGetPreferredRefreshOptionResult> {
    return this.ErrorNotifyingServer.sendRequest(new GetPreferredRefreshOptionRequest({ id: id }));
  }

  public getPreferredRefreshOptionsByUrlName(urlNames: string[]): ng.IPromise<IGetPreferredRefreshOptionByUrlNameResult> {
    return this.ErrorNotifyingServer.sendRequest(new GetPreferredRefreshOptionByUrlNameRequest({ urlNames: urlNames }));
  }

  public setRemoteQueryAgent(id: string, remoteAgentName: string, useDefaultAgent: boolean): ng.IPromise<IResult> {
    const params: ISetDatasourceRemoteQueryAgentParams = {
      id: id,
      remoteAgentName: remoteAgentName,
      useDefaultAgent: useDefaultAgent
    };

    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceRemoteQueryAgentRequest(params));
  }

  public setUsingRemoteQueryAgent(id: string): ng.IPromise<IResult> {
    const params: ISetDatasourceUsingRemoteQueryAgentParams = {
      id: id
    };

    return this.ErrorNotifyingServer.sendRequest(new SetDatasourceUsingRemoteQueryAgentRequest(params));
  }

  public changeToLive(dataSourceIds: string[], workbookId: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(dataSourceIds, (idsBlock) =>
        new ChangeExtractToLiveActionOnDatasourcesRequest(idsBlock, workbookId)
      ),
    ResultHelper.combineResults);
  }

  public cancelExtractCreationOnDatasources(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new CancelExtractCreationRequest(ids, DatasourceContentType.name)
      ),
    ResultHelper.combineResults);
  }

  public async checkConnections(dataSources: Set<IDataSourceInfo>): Promise<ICheckConnectionResult[]>  {
    const dataSourceIdArray: IDataSourceInfo[] = dataSources.toArray();
    const connections: IDataConnectionInfo[] = await getDataConnectionInfos(dataSourceIdArray, 'datasourceId');
    const result: ICheckConnectionResult[] = [];

    const dataConnectionsResource = ServiceLocator.get<DataConnections>('DataConnections');
    for (const connection of connections) {
      const connectionDetail: RemoteConnectionDetailInfo =
          <RemoteConnectionDetailInfo> connection.connectionDetails;
      const connectionInfo: IConnectionUpdate = {
        server: connectionDetail.serverName,
        port: connectionDetail.serverPort,
        username: connectionDetail.databaseUsername,
        passwordUpdate: {
          isEmbedded: connectionDetail.hasEmbeddedPassword,
          newPassword: null
        }
      };

      const response: ICheckConnectionResult = await dataConnectionsResource.checkConnection(connection.id, connectionInfo);
      result.push(response);
    }
    return result;
  }
}

export enum RefreshOptionType {
  FeatureDisabled,
  NotSupported,
  LiveConnection,
  Server,
  OnlineSync
}

Module.instance().service('DataSources', DataSources);
